/**
 * La contraseña temporal que el servidor devuelve una sola vez.
 *
 * Sale en dos sitios —al crear una cuenta y al restablecerla— y estaba escrita
 * dos veces en `Usuarios.tsx`, palabra por palabra. Aquí va una, con el botón
 * de copiar que faltaba: se muestra **una única vez**, así que obligar a
 * transcribirla a mano de un `<code>` era pedir una errata.
 */
import { useEffect, useRef, useState } from 'react'
import { Button } from '@mpfn/ui'
import { Callout } from '@mpfn/patterns'
import { Check, Copy } from 'lucide-react'
import { avisarError } from '../lib/avisos.js'

/** Cuánto dura el acuse de «Copiada» antes de volver a ofrecer copiar. */
const ACUSE = 2000

export function ContrasenaTemporal({
  valor,
  nota,
}: {
  valor: string
  /**
   * Lo que cambia entre los dos casos. Al crear la cuenta se dice cómo
   * entregarla; al restablecerla hay que avisar además de que las sesiones
   * abiertas se cerraron, que es una consecuencia que el administrador tiene
   * que poder anticipar.
   */
  nota: string
}) {
  const [copiada, setCopiada] = useState(false)
  const temporizador = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(
    () => () => {
      clearTimeout(temporizador.current)
    },
    [],
  )

  return (
    <>
      <Callout tone="warning" title="Anote la contraseña temporal">
        {nota}
      </Callout>

      <p className="sigfisc-password-temporal">
        <code>{valor}</code>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={() => {
            /*
             * `writeText` puede fallar —falta el permiso, o el navegador no
             * considera seguro el origen—. Si falla se dice, en vez de dejar
             * al administrador creyendo que copió algo que no tiene: la
             * contraseña no vuelve a mostrarse.
             */
            navigator.clipboard.writeText(valor).then(
              () => {
                clearTimeout(temporizador.current)
                setCopiada(true)
                temporizador.current = setTimeout(() => {
                  setCopiada(false)
                }, ACUSE)
              },
              (error: unknown) => {
                avisarError(error, 'No se pudo copiar. Selecciónela y cópiela a mano.')
              },
            )
          }}
        >
          {copiada ? (
            <Check size={14} aria-hidden="true" />
          ) : (
            <Copy size={14} aria-hidden="true" />
          )}
          {copiada ? 'Copiada' : 'Copiar'}
        </Button>
        {/* El cambio de icono no lo ve quien usa lector de pantalla. `output`
            y no un `span[role=status]`: es el elemento nativo del rol. */}
        <output className="mpfn-sr-only">
          {copiada ? 'Contraseña copiada al portapapeles.' : ''}
        </output>
      </p>
    </>
  )
}
